import { makeStyles } from 'tss-react/mui';
import { ORDER_PRODUCT_ROW_VERTICAL_SPACING } from '../orders.constants';
import { OrderProductSelectionEdge } from '../products/orderProducts.types';
import {
  ORDER_PRODUCT_GRID_MIN_WIDTH,
  ORDER_PRODUCT_ROW_INLINE_SIZES,
  getOrderProductRowHeight,
  getOrderProductSelectSizing,
} from '../products/orderProductsLayout.constants';
import { OrderProductRowLayout } from '../products/orderProductsLayout.types';
import {
  getOrderProductRowGutter,
  isOrderProductRowInline,
} from '../products/orderProductsLayout.utils';
import { blendColors } from './blendColors';
import { getGridSurfaceStyles } from './gridScrollFade';
import { getOrderProductRowRadii } from './orderProductItemRadii.utils';

interface IUseStylesProps {
  layout: OrderProductRowLayout;
  hasTouchGrid?: boolean;
  highlightProduct?: boolean;
  isLastOrderProduct?: boolean;
  isAddOrderProductRowView?: boolean;
  selectionEdge?: OrderProductSelectionEdge;
}

const ROW_CHECKBOX_PADDING = 4;
export const MOBILE_ORDER_PRODUCT_EXTRA_ROOT_PADDING = 12;

const STACKED_IMAGE_AND_LABEL_SPACING = 12;
const STACKED_COLOR_SPACING = 4;
const SELECTS_GAP = 4;
const STACKED_GRID_SPACING = 12;
const MOBILE_ROW_GAP = 10;
const MOBILE_COLUMN_GAP = 12;

const MOBILE_TOTALS_AND_ACTIONS_GAP = 4;

const SELECTED_ROW_OPACITY = 0.05;
const SELECTED_ROW_HOVER_OPACITY = 0.1;

export const useOrderProductItemStyles = makeStyles<IUseStylesProps>()((theme, params) => {
  const { layout } = params;
  const isInline = isOrderProductRowInline(layout);
  const isMobile = layout === OrderProductRowLayout.MOBILE;
  const isStacked = layout === OrderProductRowLayout.STACKED;
  const isCondensed = layout === OrderProductRowLayout.CONDENSED;
  const hasStackedSelects = isStacked || isCondensed;
  const wideSizes = ORDER_PRODUCT_ROW_INLINE_SIZES[OrderProductRowLayout.WIDE];
  const rowGutter = getOrderProductRowGutter(layout);

  const selectSizing = getOrderProductSelectSizing(layout);

  const rowBackground =
    params.isAddOrderProductRowView || params.highlightProduct
      ? theme.palette.background.default
      : theme.palette.background.paper;

  const selectedRowBackground = blendColors({
    background: rowBackground,
    overlay: theme.palette.primary.main,
    opacity: SELECTED_ROW_OPACITY,
  });
  const selectedRowBackgroundHover = blendColors({
    background: rowBackground,
    overlay: theme.palette.primary.main,
    opacity: SELECTED_ROW_HOVER_OPACITY,
  });

  return {
    fixedRowHeight: {
      height: getOrderProductRowHeight({ layout, hasTouchGrid: !!params.hasTouchGrid }),
    },

    root: {
      ...(!isInline && { rowGap: MOBILE_ROW_GAP }),
      paddingLeft: theme.spacing(rowGutter.left),
      paddingRight: theme.spacing(rowGutter.right),
      ...(hasStackedSelects && { paddingTop: ORDER_PRODUCT_ROW_VERTICAL_SPACING }),
      ...(isMobile && {
        paddingTop: 16,
        paddingBottom: 12,
      }),
      ...getOrderProductRowRadii({
        selectionEdge: params.selectionEdge ?? 'none',
        isLastOrderProduct: !!params.isLastOrderProduct,
        hasHoverRadius: !params.isAddOrderProductRowView,
        selectionRadius: theme.shape.borderRadiusLg,
        cardRadius: theme.shape.borderRadiusLg,
      }),
      backgroundColor: rowBackground,
    },

    orderProductInfoContainer: isInline
      ? {}
      : {
          boxSizing: 'border-box',
          ...(isCondensed && { width: '100%' }),
          ...(!isCondensed && { flex: 1, minWidth: 0, order: 1 }),
          ...(isMobile && { flexBasis: '100%' }),
        },

    orderProductInfo: {
      ...(hasStackedSelects && { columnGap: MOBILE_COLUMN_GAP }),
      ...(isMobile && { paddingTop: 0, columnGap: MOBILE_COLUMN_GAP, rowGap: MOBILE_ROW_GAP }),
    },

    orderProductImageAndLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      ...(isInline && {
        width: wideSizes.imageAndLabel,
        marginRight: wideSizes.columnGap,
      }),
      ...(hasStackedSelects && {
        flex: 1,
        minWidth: 0,
        marginRight: STACKED_IMAGE_AND_LABEL_SPACING,
      }),
      ...(isMobile && { flex: 1, minWidth: 0 }),
    },

    orderProductButtons: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: SELECTS_GAP,
      '& .MuiButton-root': {
        border: `1px solid ${theme.palette.custom.border.secondary}`,
      },
      ...(hasStackedSelects && { flex: '0 0 auto' }),
      ...(isMobile && { flexDirection: 'row', columnGap: SELECTS_GAP, width: '100%' }),
    },

    orderProductColor: {
      ...selectSizing,
      ...(isInline && { marginRight: wideSizes.columnGap }),
      ...(hasStackedSelects && { marginRight: STACKED_COLOR_SPACING }),
      ...(isMobile && { marginRight: 0 }),
    },

    orderProductDeliveryWindow: {
      ...selectSizing,
      ...(isInline && { marginRight: wideSizes.columnGap }),
      ...(!isInline && { marginRight: 0 }),
    },

    orderProductMobileTotals: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      columnGap: MOBILE_TOTALS_AND_ACTIONS_GAP,
      flexShrink: 0,
    },

    orderProductGridContainer: {
      flex: 1,
      overflowX: isMobile ? 'visible' : 'auto',
      ...(isInline && {
        minWidth: ORDER_PRODUCT_GRID_MIN_WIDTH,
        marginRight: wideSizes.gridGap,
      }),
      ...(isCondensed && { flex: 1, minWidth: 0 }),
      ...(!isInline && !isCondensed && { flex: '1 1 100%', minWidth: 0, order: 3 }),
      ...(hasStackedSelects && { marginRight: STACKED_GRID_SPACING }),
      ...(isMobile && { marginRight: 0, marginLeft: 0 }),
    },

    orderProductTotalsAndActionsWrapper: {
      ...(!isInline && !isCondensed && { order: 2, flexShrink: 0 }),
      ...(isCondensed && { flexShrink: 0 }),
    },

    orderProductTotalsAndActionsContainer: {
      display: 'flex',
      columnGap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: isMobile ? 'flex-end' : 'center',
    },

    boxContainer: {
      ...(isInline && { gridColumnGap: theme.spacing(1) }),
      ...(isStacked && { marginRight: -10 }),
    },

    selectedRow: {
      ...getGridSurfaceStyles({ base: selectedRowBackground }),

      '&:hover': {
        ...getGridSurfaceStyles({ base: selectedRowBackgroundHover }),
      },
    },

    checkbox: {
      padding: ROW_CHECKBOX_PADDING,
    },
  };
});
